import { injectable } from 'inversify';
import { compact } from 'lodash-es';
import {
    ALL_DELEGATIONSTATE,
    CustomFieldWithConditionFragment,
    DelegationsCategory,
    DelegationsSortAttributes,
    EventId,
    FilterType,
    OrganizationId,
    SegmentCustomFieldFragment
} from '../../generated/types';
import { CountriesService } from '../../services/countriesService';
import { DateTimeService } from '../../services/dateTimeService';
import { TranslationService } from '../../services/translationService';
import { isNonEmptyArray } from '../../util/array';
import { Emptyable } from '../../util/emptyable';
import { Filter, PossibleColumn, SegmentService } from '../segment';

export enum DelegationDefaultColumns {
    AssignedResources = 'resources',
    Campaigns = 'campaigns',
    CanLeadersAddMembers = 'canLeadersAddMembers',
    CanLeadersDeleteMembers = 'canLeadersDeleteMembers',
    CanLeadersEditColumns = 'canLeadersEditColumns',
    CanLeadersEditCustomFields = 'canLeadersEditCustomFields',
    CanLeadersEditMembers = 'canLeadersEditMembers',
    CreatedAt = 'createdAt',
    CreatedBy = 'createdBy',
    DeadlineDate = 'deadlineDate',
    DelegationCategory = 'delegationCategory',
    Description = 'description',
    Documents = 'documents',
    Events = 'events',
    FillingRate = 'fillingRate',
    Form = 'form',
    Id = 'id',
    IsLocked = 'isLocked',
    Leaders = 'leaders',
    MaxResources = 'maxResources',
    Name = 'name',
    PrivateNote = 'privateNote',
    ShowFormLinkInDelegationSpace = 'showFormLinkInDelegationSpace',
    ShowInDelegation = 'showInDelegation',
    State = 'delegationState',
    Tags = 'tags'
}

export function columnsToIncludes(columns: string[]) {
    return {
        includeCampaigns: columns.includes(DelegationDefaultColumns.Campaigns),
        includeChildren: columns.includes(DelegationDefaultColumns.Events),
        includeCreatedBy: columns.includes(DelegationDefaultColumns.CreatedBy),
        includeDocuments: columns.includes(DelegationDefaultColumns.Documents),
        includeLeaders: columns.includes(DelegationDefaultColumns.Leaders),
        includeResources:
            columns.includes(DelegationDefaultColumns.FillingRate) ||
            columns.includes(DelegationDefaultColumns.AssignedResources),
        includeTags: columns.includes(DelegationDefaultColumns.Tags)
    };
}

@injectable()
export class DelegationsSegmentsService extends SegmentService {
    constructor(
        countriesService: CountriesService,
        dateTimeService: DateTimeService,
        translationService: TranslationService
    ) {
        super(countriesService, dateTimeService, translationService);
    }

    getDelegationsFilters(
        organizationId: OrganizationId,
        eventId: Emptyable<EventId>,
        customFields: SegmentCustomFieldFragment[],
        delegationsCategories: Array<Pick<DelegationsCategory, 'id' | 'name'>>
    ): Filter[] {
        return compact([
            {
                slug: DelegationDefaultColumns.Name,
                name: this.t('nom_de_la_d_l_g_28087'),
                category: this.t('d_l_gations_78318'),
                fieldType: FilterType.Text,
                hideEmpty: true
            },
            {
                slug: DelegationDefaultColumns.Description,
                name: this.t('Description'),
                category: this.t('d_l_gations_78318'),
                fieldType: FilterType.Text
            },
            {
                slug: DelegationDefaultColumns.PrivateNote,
                name: this.t('note_priv_e_06303'),
                category: this.t('d_l_gations_78318'),
                fieldType: FilterType.Text
            },
            isNonEmptyArray(delegationsCategories)
                ? {
                      slug: DelegationDefaultColumns.DelegationCategory,
                      name: this.t('cat_gorie_00291'),
                      category: this.t('d_l_gations_78318'),
                      fieldType: FilterType.Select,
                      values: delegationsCategories,
                      hideEmpty: true
                  }
                : null,
            {
                slug: 'delegationState',
                name: this.t('status_06428'),
                category: this.t('d_l_gations_78318'),
                fieldType: FilterType.Select,
                values: ALL_DELEGATIONSTATE.map((state) => ({
                    id: state,
                    name: state
                })),
                hideEmpty: true
            },
            {
                slug: DelegationDefaultColumns.Leaders,
                name: this.t('reponsable_82458_plural'),
                category: this.t('d_l_gations_78318'),
                fieldType: FilterType.Select,
                needLoading: true,
                organizationId,
                eventId
            },
            {
                slug: 'form',
                name: this.t('Form'),
                category: this.t('d_l_gations_78318'),
                fieldType: FilterType.Select,
                hideEmpty: true,
                needLoading: true,
                organizationId,
                eventId
            },
            {
                slug: 'deadlineDate',
                name: this.t('date_limite_d_i_79779'),
                category: this.t('d_l_gations_78318'),
                fieldType: FilterType.Date
            },
            {
                slug: 'tag',
                name: this.t('Tag'),
                category: this.t('d_l_gations_78318'),
                fieldType: FilterType.Select,
                needLoading: true,
                organizationId
            },
            {
                slug: 'resources',
                name: this.t('nombre_de_membr_08851'),
                category: this.t('d_l_gations_78318'),
                fieldType: FilterType.Number,
                hideEmpty: true
            },
            {
                slug: 'fillingRate',
                name: this.t('taux_de_remplis_11913'),
                category: this.t('d_l_gations_78318'),
                fieldType: FilterType.Number,
                hideEmpty: true
            },
            {
                slug: DelegationDefaultColumns.ShowInDelegation,
                name: this.t('ShowInDelegationSpace'),
                category: this.t('d_l_gations_78318'),
                fieldType: FilterType.Checkbox,
                hideEmpty: true
            },
            {
                slug: DelegationDefaultColumns.CanLeadersAddMembers,
                name: this.t('CanLeadersAddMembers'),
                category: this.t('d_l_gations_78318'),
                fieldType: FilterType.Checkbox,
                hideEmpty: true
            },
            {
                slug: DelegationDefaultColumns.CanLeadersDeleteMembers,
                name: this.t('CanLeadersAddMembers'),
                category: this.t('d_l_gations_78318'),
                fieldType: FilterType.Checkbox,
                hideEmpty: true
            },
            {
                slug: DelegationDefaultColumns.CanLeadersEditMembers,
                name: this.t('CanLeadersEditMembers'),
                category: this.t('d_l_gations_78318'),
                fieldType: FilterType.Checkbox,
                hideEmpty: true
            },
            {
                slug: DelegationDefaultColumns.CanLeadersEditColumns,
                name: this.t('CanLeadersAddMembers'),
                category: this.t('d_l_gations_78318'),
                fieldType: FilterType.Checkbox,
                hideEmpty: true
            },
            {
                slug: DelegationDefaultColumns.CanLeadersEditCustomFields,
                name: this.t('CanLeadersEditCustomFields'),
                category: this.t('d_l_gations_78318'),
                fieldType: FilterType.Checkbox,
                hideEmpty: true
            },
            {
                slug: DelegationDefaultColumns.ShowFormLinkInDelegationSpace,
                name: this.t('afficher_le_lie_05841'),
                category: this.t('d_l_gations_78318'),
                fieldType: FilterType.Checkbox,
                hideEmpty: true
            },
            eventId
                ? {
                      slug: 'accreditation',
                      name: this.t('accr_ditations_39450'),
                      category: this.t('d_l_gations_78318'),
                      fieldType: FilterType.Select,
                      needLoading: true,
                      eventId
                  }
                : null,
            {
                slug: DelegationDefaultColumns.CreatedBy,
                name: this.t('cr_e_par_30622'),
                category: this.t('d_l_gations_78318'),
                fieldType: FilterType.Select,
                hideEmpty: true,
                needLoading: true,
                organizationId,
                eventId
            },
            {
                slug: DelegationDefaultColumns.CreatedAt,
                name: this.t('cr_e_le_02658'),
                category: this.t('d_l_gations_78318'),
                fieldType: FilterType.Datetime,
                hideEmpty: true
            },
            eventId
                ? null
                : {
                      slug: 'event',
                      name: this.t('_v_nement_22345'),
                      category: this.t('Event'),
                      fieldType: FilterType.Select,
                      hideEmpty: true,
                      needLoading: true,
                      organizationId
                  },
            ...this.customFieldsToFilters(customFields)
        ]);
    }

    getDelegationsPossibleColumns(
        customFields: SegmentCustomFieldFragment[] | CustomFieldWithConditionFragment[]
    ): PossibleColumn[] {
        const possibleColumns: PossibleColumn[] = [
            {
                slug: DelegationDefaultColumns.Id,
                name: this.t('id_51738'),
                isSearchable: true,
                isSortable: true,
                sortAttribute: DelegationsSortAttributes.Id,
                width: 100
            },
            {
                slug: DelegationDefaultColumns.Name,
                name: this.t('nom_de_la_d_l_g_28087'),
                isSearchable: true,
                isSortable: true,
                sortAttribute: DelegationsSortAttributes.Name
            },
            {
                slug: DelegationDefaultColumns.DelegationCategory,
                name: this.t('cat_gorie_00291'),
                isSearchable: true,
                isSortable: true,
                sortAttribute: DelegationsSortAttributes.Category
            },
            {
                slug: DelegationDefaultColumns.State,
                name: this.t('status_06428')
            },
            {
                slug: DelegationDefaultColumns.Leaders,
                name: this.t('reponsable_82458_plural'),
                isSearchable: true
            },
            {
                slug: DelegationDefaultColumns.Form,
                name: this.t('Form'),
                isSearchable: true
            },
            {
                slug: DelegationDefaultColumns.DeadlineDate,
                name: this.t('date_limite_d_i_79779')
            },
            {
                slug: DelegationDefaultColumns.MaxResources,
                name: this.t('MaxResources')
            },
            {
                slug: DelegationDefaultColumns.AssignedResources,
                name: this.t('membres_11310')
            },
            {
                slug: DelegationDefaultColumns.Tags,
                name: this.t('tags_79499'),
                isSearchable: true
            },
            {
                slug: DelegationDefaultColumns.FillingRate,
                name: this.t('taux_de_remplis_11913')
            },
            {
                slug: DelegationDefaultColumns.CreatedBy,
                name: this.t('cr_e_par_30622'),
                isSearchable: true
            },
            {
                slug: DelegationDefaultColumns.CreatedAt,
                name: this.t('cr_e_le_02658'),
                isSortable: true,
                sortAttribute: DelegationsSortAttributes.CreatedAt
            },
            {
                slug: DelegationDefaultColumns.ShowInDelegation,
                name: this.t('ShowInDelegationSpace')
            },
            {
                slug: DelegationDefaultColumns.CanLeadersAddMembers,
                name: this.t('CanLeadersAddMembers')
            },
            {
                slug: DelegationDefaultColumns.CanLeadersEditMembers,
                name: this.t('CanLeadersEditMembers')
            },
            {
                slug: DelegationDefaultColumns.CanLeadersDeleteMembers,
                name: this.t('CanLeadersDeleteMembers')
            },
            {
                slug: DelegationDefaultColumns.CanLeadersEditColumns,
                name: this.t('CanLeadersEditColumns')
            },
            {
                slug: DelegationDefaultColumns.CanLeadersEditCustomFields,
                name: this.t('CanLeadersEditCustomFields')
            },
            {
                slug: DelegationDefaultColumns.ShowFormLinkInDelegationSpace,
                name: this.t('afficher_le_lie_05841')
            },
            {
                slug: DelegationDefaultColumns.IsLocked,
                name: this.t('IsLocked')
            },
            {
                slug: DelegationDefaultColumns.Events,
                name: this.t('_v_nements_53672')
            },
            {
                slug: DelegationDefaultColumns.Campaigns,
                name: 'Campagnes que les responsables peuvent envoyer'
            },
            {
                slug: DelegationDefaultColumns.Documents,
                name: 'Documents que les responsables peuvent télécharger'
            }
        ];

        return possibleColumns.concat(this.customFieldsToPossibleColumns(customFields));
    }
}
